function getColor(row: number, col: number): string {
  const i = col % 8;
  const rowTranslation = 8 - (row % 8);
  const v = i + rowTranslation;

  switch (v % 8) {
    case 0:
      return "#62AC9B"; // green
    case 1:
      return "#D0E6A4"; // light green
    case 2:
      return "#E27165"; // red
    case 3:
      return "#EECCBB"; // light pink
    case 4:
      return "#5B8EB6"; // blue
    case 5:
      return "#BACADF"; // light blue
    case 6:
      return "#F2AE72"; // orange
    case 7:
    default:
      return "#F2E494"; // yellow
  }
}

function getText(row: number, col: number) {
  return `${String.fromCharCode(65 + row)}${col + 1}`;
}

function drawSquare(context: CanvasRenderingContext2D, row: number, col: number, squareSize: number) {
  context.fillStyle = getColor(row, col);
  context.fillRect(col * squareSize, row * squareSize, squareSize, squareSize);

  const fontSize = Math.floor(squareSize / 3);
  context.font = `${fontSize}px Arial`;
  context.fillStyle = "black";
  context.textBaseline = "middle";
  context.textAlign = "center";
  context.fillText(getText(row, col), (col + 0.5) * squareSize, (row + 0.5) * squareSize);
}

function drawQuadrant(
  context: CanvasRenderingContext2D,
  quadrant: number,
  canvasSize: number,
  squareSize: number,
  color: string,
) {
  const mid = canvasSize / 2;
  const bandSize = squareSize / 4;

  let startX: number = 0,
    startY: number = 0;
  switch (quadrant) {
    case 0: // TL
      startX = startY = 0;
      break;
    case 1: // TR
      startX = mid;
      startY = 0;
      break;
    case 2: // BL
      startX = 0;
      startY = mid;
      break;
    case 3: // BR
      startX = startY = mid;
      break;
  }

  for (let border = 0; border < bandSize; border++) {
    const inc = mid - 1 - 2 * border;
    const x = startX + border;
    const y = startY + border;

    context.strokeStyle = border === 0 ? color : "rgba(255, 255, 255, 0.7)";
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + inc, y);
    context.lineTo(x + inc, y + inc);
    context.lineTo(x, y + inc);
    context.lineTo(x, y);
    context.stroke();
  }
}

export function renderCalibrationTexture(context: CanvasRenderingContext2D, canvasSize: number) {
  const squareCount = 24;
  const squareSize = canvasSize / squareCount;

  context.lineWidth = 1;
  context.imageSmoothingEnabled = false;
  context.translate(0.5, 0.5);

  for (let row = 0; row < squareCount; row++) {
    for (let col = 0; col < squareCount; col++) {
      drawSquare(context, row, col, squareSize);
    }
  }

  drawQuadrant(context, 0, canvasSize, squareSize, "red");
  drawQuadrant(context, 1, canvasSize, squareSize, "blue");
  drawQuadrant(context, 2, canvasSize, squareSize, "green");
  drawQuadrant(context, 3, canvasSize, squareSize, "orange");
}

export async function getCalibrationTextureFromCanvas(tilingSize: number, useGLTFViewerTextureOrientation: boolean) {
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = tilingSize;
  const context = canvas.getContext("2d");

  // glTF's spec and glTF viewers expect UVs to be mapped from top-left to bottom-right.
  //
  // To support tiling UVs in this context, we need to Y-flip the canvas so the image is
  // embedded flipped.
  //
  // NOTE: Maya expects non-flipped textures, so only glTF default mode uses this texture orientation.
  if (context) {
    if (useGLTFViewerTextureOrientation) {
      context.transform(1, 0, 0, -1, 0, canvas.height);
    }

    renderCalibrationTexture(context, tilingSize);
  }

  const blob = await new Promise<Blob>((resolve) => canvas.toBlob(resolve as any));
  return await blob.arrayBuffer();
}

export function getSize128CalibrationDebugDataURL(): string {
  return "data:image/png;base64,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";
}
